import { CurrentUser, getAccessToken } from 'auth';
import axios, { AxiosResponse } from 'axios';
import { get } from 'lodash';
import {
  AuditLogApi,
  CommodityCodesApi,
  CompanyApi,
  CompanyListsApi,
  ContentApi,
  GeolocationApi,
  KosmosCompanyApi,
  KosmosReportApi,
  NotificationApi,
  OrganisationScoredCompaniesApi,
  OrganisationsApi,
  QuestionnaireApi,
  ScoreApi,
  SectorCodesApi,
  SystemTestApi,
  TempTasksApi,
  TransactionApi,
  UserApi,
  WomeApplicationsApi,
} from 'models/generated/api';
import { Configuration } from 'models/generated/configuration';
import { API_URL } from 'projectConfig';

export async function getApiConfiguration() {
  const locale = CurrentUser.getInstance().getLocale() || 'en-GB';
  const token = await getAccessToken();
  if (!token) {
    throw new Error('cannot find valid access token');
  }
  return new Configuration({
    basePath: API_URL.replace(/\/+$/, ''),
    baseOptions: {
      headers: {
        'Accept-Language': locale,
        Authorization: 'Bearer ' + token.accessToken,
      },
    },
  });
}

export function getPublicApiConfiguration() {
  const locale = CurrentUser.getInstance().getLocale() || 'en-GB';
  return new Configuration({
    basePath: API_URL.replace(/\/+$/, ''),
    baseOptions: {
      headers: {
        'Accept-Language': locale,
      },
    },
  });
}

export const handleError = (e: unknown) => {
  if (axios.isAxiosError(e) && e.response) {
    console.log(e.response?.data);
    const errorMessage = get(e.response?.data, 'message') || 'Something went wrong';
    throw new Error(errorMessage);
  } else {
    // native error
    throw e;
  }
};

type IFunc<T, K> = (params: T) => Promise<K>;

export const execute = async <T, K>(fn: IFunc<T, AxiosResponse<K>>, params: T) => {
  try {
    const response = await fn(params);
    return response.data;
  } catch (e) {
    return handleError(e);
  }
};

export const getAuditLogApi = async () => new AuditLogApi(await getApiConfiguration());
export const getCommodityCodesApi = async () => new CommodityCodesApi(await getApiConfiguration());
export const getCompanyApi = async () => new CompanyApi(await getApiConfiguration());
export const getTempTasksApi = async () => new TempTasksApi(await getApiConfiguration());
export const getCompanyListsApi = async () => new CompanyListsApi(await getApiConfiguration());
export const getContentApi = () => new ContentApi(getPublicApiConfiguration());
export const getGeolocationApi = async () => new GeolocationApi(await getApiConfiguration());
export const getKosmosCompanyApi = async () => new KosmosCompanyApi(await getApiConfiguration());
export const getWomeApplicationsApi = async () =>
  new WomeApplicationsApi(await getApiConfiguration());
export const getKosmosReportApi = async () => new KosmosReportApi(await getApiConfiguration());
export const getNotificationApi = async () => new NotificationApi(await getApiConfiguration());
export const getOrganisationScoredCompaniesApi = async () =>
  new OrganisationScoredCompaniesApi(await getApiConfiguration());
export const getOrganisationsApi = async () => new OrganisationsApi(await getApiConfiguration());
export const getQuestionnaireApi = async () => new QuestionnaireApi(await getApiConfiguration());
export const getScoreApi = async () => new ScoreApi(await getApiConfiguration());
export const getSectorCodesApi = async () => new SectorCodesApi(await getApiConfiguration());
export const getSystemTestApi = async () => new SystemTestApi(await getApiConfiguration());
export const getUserApi = async () => new UserApi(await getApiConfiguration());
export const getTransactionApi = async () => new TransactionApi(await getApiConfiguration());
